import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { fetchDeployedBundlePath, loadedBundlePath } from 'utils/appVersion'

const CHECK_INTERVAL_MS = 10 * 60 * 1000
/** Floor between checks, so returning to the tab a dozen times does not mean a dozen requests. */
const MIN_GAP_MS = 60 * 1000
/** Exported so a test can ask whether the prompt is up: a dismissed toast lingers in jsdom,
 * which never fires the exit transition, but it stops being active the moment it is waved off. */
export const NEW_VERSION_TOAST_ID = 'new-version-available'

const Prompt = ({ onLater }: { onLater: () => void }) => (
  <div className="text-[14px]">
    <p className="font-semibold">A new version of Nexys is available.</p>
    <p className="mt-0.5 opacity-90">Reload to pick it up. Anything you have typed and not saved will be lost.</p>
    <div className="mt-2 flex gap-2">
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-white/95 px-2.5 py-1 text-[12px] font-semibold text-shade-blue transition hover:bg-white"
      >
        Reload now
      </button>
      <button
        type="button"
        onClick={onLater}
        className="rounded-md border border-white/70 px-2.5 py-1 text-[12px] font-medium text-white transition hover:bg-white/10"
      >
        Later
      </button>
    </div>
  </div>
)

/**
 * Tells the user when the tab is running an older release than the site is serving.
 *
 * Renders nothing until a deploy lands under an open tab, then raises one toast that stays up until
 * it is answered. "Later" holds its peace for that release and speaks up again only for the next
 * one, so a long session is not nagged.
 *
 * Under the dev server there is no hashed main bundle, so `loadedBundlePath()` is null and this
 * never runs; it only has an opinion in a real build. See `utils/appVersion`.
 */
export const NewVersionPrompt = ({
  intervalMs = CHECK_INTERVAL_MS,
  minGapMs = MIN_GAP_MS,
}: {
  /** Both are here so a test can run the loop in milliseconds; the app uses the defaults. */
  intervalMs?: number
  minGapMs?: number
}) => {
  const dismissed = useRef<string | null>(null)
  const lastCheckedAt = useRef(0)

  useEffect(() => {
    const loaded = loadedBundlePath()
    if (!loaded) return

    const controller = new AbortController()
    let stopped = false

    const check = async () => {
      if (stopped || document.hidden) return
      if (Date.now() - lastCheckedAt.current < minGapMs) return
      lastCheckedAt.current = Date.now()

      const deployed = await fetchDeployedBundlePath(controller.signal)
      if (stopped || !deployed || deployed === loaded || deployed === dismissed.current) return

      toast(
        <Prompt
          onLater={() => {
            dismissed.current = deployed
            toast.dismiss(NEW_VERSION_TOAST_ID)
          }}
        />,
        { toastId: NEW_VERSION_TOAST_ID, type: 'info', autoClose: false, closeOnClick: false, closeButton: false, draggable: false },
      )
    }

    const onVisibilityChange = () => {
      if (!document.hidden) check()
    }

    const timer = window.setInterval(check, intervalMs)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      stopped = true
      controller.abort()
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [intervalMs, minGapMs])

  return null
}
